import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import InputMask from 'inputmask';
import { BehaviorSubject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { Components } from '@ispui/input';

import { setAutofocus } from './input.utils';

import { ISPFieldType } from '../../model';
import { ISPFieldTypeBase } from '../field-type-base.class';

/**
 * `@type=text` input field component
 *
 * Use only with Formly
 */
@UntilDestroy()
@Component({
  selector: 'isp-formly-input-field',
  templateUrl: './input.component.html',
  styleUrls: ['./scss/input.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputFieldComponent
  extends ISPFieldTypeBase<ISPFieldType.Input>
  implements OnInit, AfterViewInit
{
  /** Whether the field control is expanded */
  private readonly isZoomedSubject = new BehaviorSubject(false);

  /** Input mask controller */
  private mask?: InputMask.Instance;

  /** Value for expanded field */
  private fieldValue: string;

  /** Field expanded observable */
  readonly isZoomed$ = this.isZoomedSubject.asObservable();

  /** Input element reference */
  @ViewChild('inputElement')
  inputRef: ElementRef<HTMLInputElement>;

  /** Input element reference */
  @ViewChild('ispuiInputElement')
  ispuiInputRef: ElementRef<Components.IspuiInput>;

  /**
   * Set mask to input
   *
   * @param inputControl - input html element, on which mask will be applied
   * @param mask - mask value
   */
  private setMask(inputControl: HTMLInputElement, mask?: string): void {
    this.mask = InputMask({
      mask,
      showMaskOnHover: false,
      placeholder: '',
    });
    this.mask.mask(inputControl);
  }

  /**
   * Subscribe to field toggle, for synchronize field value
   */
  ngOnInit(): void {
    super.ngOnInit();

    this.formControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      // @HACK i.ablov ispui-input cannot catch input value change, because angular reactive form change do not emit any 'input' or 'change' event
      // on FormControl change. So we trigger it by ourself
      this.ispuiInputRef?.nativeElement.validateInput();
    });

    this.isZoomed$
      .pipe(
        filter(Boolean),
        tap(() => {
          this.fieldValue = this.formControl.value.replace(/\s+/g, '\n');
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.props.autofocus) {
      await setAutofocus(
        this.inputRef.nativeElement,
        this.field,
        this.formState,
      );
    }

    if (this.props.mask) {
      this.setMask(this.inputRef.nativeElement, this.props.mask);
    }
  }

  get zoomButtonIcon(): 'ff-unzoom-a' | 'ff-zoom-a' {
    return this.isZoomedSubject.value ? 'ff-unzoom-a' : 'ff-zoom-a';
  }

  handleTextareaBlur(): void {
    this.formControl.markAsDirty();
    this.formControl.updateValueAndValidity();
  }

  /**
   * Access to control value
   */
  get controlValue(): string {
    return this.fieldValue;
  }

  set controlValue(newValue: string) {
    // remove validator before update value,
    // because validation will be triggered on each field change
    const validator = this.formControl.asyncValidator;
    this.formControl.asyncValidator = null;
    this.formControl.patchValue(newValue.replace(/\n+/g, ' '), {
      emitEvent: false,
    });
    this.formControl.asyncValidator = validator;
  }

  /**
   * Toggle the zoom expansion/collapse
   */
  toggleZoom(): void {
    this.isZoomedSubject.next(!this.isZoomedSubject.value);
  }

  /**
   * Set unlimited value to control
   */
  setUnlimitedValue(): void {
    this.formControl.setValue(this.props.unlimitedValue);
  }
}
